import React, { useState, useEffect } from "react"
import { useDispatch, connect } from "react-redux"
import { Link, useLocation } from "react-router-dom"
import { ArrowUp, ArrowDown } from "lucide-react"
import { tabManager } from "../../sidebar/sidebarSlice"
import Skelton from "../skeleton/Skeleton"
import Loading from "../../loading/LoadingNew"
import { generateKey } from "../../../sharedfiles/helper"
import { useColumnPreferences } from "../../../sharedfiles/hooks/useColumnPreferences"
import Tooltips from "../Tooltip"
import { Table as ShadcnTable, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../ui/table"
import { Button } from "../../../ui/button"
import { cn } from "../../../lib/utils"

/**
 * Table component - Migrated to use shadcn/ui Table
 * Complex data table with sorting, linking, and action buttons
 * 
 * @param {Array} tableData - Table data: [headers, tooltips, ...rows]
 * @param {number|Array} linkIndex - Column index(es) that should be links
 * @param {Array} rowData - Row-specific data
 * @param {boolean} loader - Show loading state
 * @param {Array} styles - Column styles array
 * @param {number} hLen - Header length for skeleton
 * @param {number} vLen - Vertical length for skeleton
 * @param {string} skeltonWidth - Skeleton width
 * @param {object} sortables - Sortable columns config
 * @param {number} viewBtn - Column index for view button
 * @param {function} onViewBtn - View button handler
 * @param {string} BtnName - View button label
 * @param {number} cancelBtn - Column index for cancel button
 * @param {function} onCancel - Cancel button handler
 * @param {string} CancelbtnName - Cancel button label
 * @param {boolean} btnStatus - Button status flag
 * @param {function} onLinkClick - Custom link click handler
 * @param {number|Array} clickIndex - Column index(es) that should open a custom action (e.g. a details popup) on click, independent of linkIndex
 * @param {function} onCellClick - Handler invoked as onCellClick(cellValue, columnIndex) for clickIndex columns
 * @param {function} handleSortFields - Sort handler
 * @param {object} sortingOrder - Current sorting order
 * @param {Array} tabStack - Tab stack from Redux
 * @param {string} storageKey - Key for persisting column show/hide + order (falls back to scrollKey, then route pathname)
 */
const Table = (props) => {
  const {
    tableData,
    linkIndex,
    rowData,
    loader,
    styles,
    hLen,
    vLen,
    skeltonWidth,
    sortables,
    viewBtn,
    onViewBtn,
    BtnName,
    cancelBtn,
    onCancel,
    CancelbtnName,
    btnStatus,
    onLinkClick,
    clickIndex,
    onCellClick,
    dateWidthIndex = [],
    withCard = false,
    scrollKey,
    storageKey,
    ...rest
  } = props

  const dispatch = useDispatch()
  const [hideLoader, setHideLoader] = useState(true)
  const location = useLocation()

  const tabsChange = (item) => {
    dispatch(tabManager(+item, props.tabStack))
    localStorage.setItem("userId", item)
  }

  // Cell values are normally primitives; plain objects are shown as JSON for
  // visibility, but a React element (e.g. an icon + text) must render as-is.
  const renderCellValue = (Cid) =>
    React.isValidElement(Cid) ? Cid : Cid && typeof Cid === "object" ? JSON.stringify(Cid) : Cid

  useEffect(() => {
    const timer = setTimeout(() => {
      setHideLoader(false)
    }, 5000)

    return () => {
      clearTimeout(timer)
    }
  }, [])

  // headers must be computed unconditionally (before any early return) so the
  // hook below always runs, per the rules of hooks - it degrades gracefully
  // to an empty column list while tableData isn't ready yet.
  const headers = Array.isArray(tableData) ? tableData[0] || [] : []
  const { visibleIndices } = useColumnPreferences(
    storageKey || scrollKey || location.pathname,
    headers
  )

  // Show skeleton while loading or no data
  if (
    tableData === null ||
    tableData === undefined ||
    (Array.isArray(tableData) && tableData?.length === 0)
  ) {
    if (hideLoader) {
      return <Skelton hLen={hLen} vLen={vLen} width={skeltonWidth} />
    }
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-muted-foreground text-base">No Data Found</div>
      </div>
    )
  }

  // No valid table data
  if (!Array.isArray(tableData) || tableData?.length === 0) {
    return null
  }

  const tooltips = tableData[1] || []
  const rows = tableData.slice(2)

  const inner = (
    <ShadcnTable scrollKey={scrollKey}>
        <TableHeader>
          <TableRow>
            {visibleIndices.map((origIdx, ind) => {
              const item = headers[origIdx]
              const isSortable = sortables && Object.keys(sortables).includes(item)
              return (
                <TableHead
                  key={generateKey(item)}
                  className={cn(
                    "text-center",
                    isSortable && "cursor-pointer select-none hover:bg-muted/50"
                  )}
                  onClick={() =>
                    isSortable ? rest.handleSortFields(sortables[item], item) : null
                  }
                >
                  <div className="flex items-center justify-center gap-2">
                    <span className="font-semibold">{item}</span>
                    {tooltips[origIdx] && <Tooltips info={`${tooltips[origIdx]}`} placement="left" />}
                    {isSortable && (
                      <>
                        {rest.sortingOrder && rest.sortingOrder[item] ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )}
                      </>
                    )}
                  </div>
                </TableHead>
              )
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length > 0 && rows[0] !== undefined && rows[0] !== null && rows[0].length > 0 ? (
            rows.map((item, ind) => {
              const rowFlags = tableData.rowFlags && tableData.rowFlags[ind]
              return (
                <TableRow key={generateKey(ind)} className="hover:bg-muted/30">
                  {visibleIndices.map((origIdx, tdIndex) => {
                    const Cid = item[origIdx]

                    // Link column
                    if (
                      Array.isArray(linkIndex)
                        ? linkIndex.includes(origIdx)
                        : origIdx === linkIndex
                    ) {
                      return (
                        <TableCell
                          key={generateKey(tdIndex)}
                          className={cn("text-center", styles ? styles[origIdx] : "")}
                        >
                          {rowFlags?.isBetSelectionAvailable === false ? (
                            <span>{renderCellValue(Cid)}</span>
                          ) : onLinkClick ? (
                            <a
                              href="javascript:void(0);"
                              onClick={() => onLinkClick(Cid, origIdx)}
                              className="text-primary underline cursor-pointer hover:text-primary/80"
                            >
                              {renderCellValue(Cid)}
                            </a>
                          ) : (
                            <Link
                              to={`/customerid/${Cid}`}
                              onClick={() => tabsChange(Cid)}
                              className="text-primary underline hover:text-primary/80"
                            >
                              {renderCellValue(Cid)}
                            </Link>
                          )}
                        </TableCell>
                      )
                    }

                    // Custom click-action column (e.g. opens a details popup) -
                    // separate from linkIndex so it never affects Link-based columns
                    if (
                      Array.isArray(clickIndex)
                        ? clickIndex.includes(tdIndex)
                        : tdIndex === clickIndex
                    ) {
                      return (
                        <TableCell
                          key={generateKey(tdIndex)}
                          className={cn("text-center", styles ? styles[tdIndex] : "")}
                        >
                          {Cid !== null && Cid !== undefined && Cid !== "" ? (
                            <span
                              onClick={() => onCellClick && onCellClick(Cid, tdIndex)}
                              className="text-primary underline cursor-pointer hover:text-primary/80"
                            >
                              {Cid && typeof Cid === "object" ? JSON.stringify(Cid) : Cid}
                            </span>
                          ) : (
                            <span>{Cid}</span>
                          )}
                        </TableCell>
                      )
                    }

                    // View button column
                    if (origIdx === viewBtn) {
                      return (
                        <TableCell key={generateKey(tdIndex)} className="text-center">
                          <Button
                            id="onViewBtn"
                            onClick={() => onViewBtn(item[0])}
                            size="sm"
                            variant="default"
                          >
                            {BtnName}
                          </Button>
                        </TableCell>
                      )
                    }

                    // Cancel button column
                    if (origIdx === cancelBtn) {
                      const isEnabled =
                        item[13] === "INIT" &&
                        item[10] === "WITHDRAW"

                      return (
                        <TableCell key={generateKey(tdIndex)} className="text-center">
                          <Button
                            id="onCancelBtn"
                            onClick={() => onCancel(item[0])}
                            disabled={!isEnabled}
                            size="sm"
                            variant="secondary"
                          >
                            {CancelbtnName}
                          </Button>
                        </TableCell>
                      )
                    }

                    // Regular data cell
                    const isDateCol = dateWidthIndex && dateWidthIndex.includes(origIdx);
                    return (
                      <TableCell
                        key={generateKey(tdIndex)}
                        className={cn("text-center", styles ? styles[origIdx] : "")}
                        style={{
                          width: isDateCol ? "120px" : "auto",
                          whiteSpace: isDateCol ? "nowrap" : "normal",
                        }}
                      >
                        {renderCellValue(Cid)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })
          ) : loader ? (
            <TableRow>
              <TableCell colSpan={31} className="text-center">
                <Loading isLoader={true} />
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell colSpan={100} className="text-center">
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="text-muted-foreground text-base">No Data Found</div>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </ShadcnTable>
  )

  return withCard ? (
    <div className="rounded-lg border bg-card shadow-sm">{inner}</div>
  ) : inner
}

const mapStateToProps = (state) => {
  return {
    tabStack: state.sidebar.tabStack,
  }
}

export default connect(mapStateToProps)(Table);